import OTPEntry from '@/components/OTPEntry';
import ArtistTaxonomyFields from '@/components/ArtistTaxonomyFields';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import LaunchTrustSignals from '@/components/LaunchTrustSignals';
import PolicyConsentChecklist, { emptyPolicyConsent, hasAcceptedAllPolicies } from '@/components/PolicyConsentChecklist';
import {
  artistCategoryOptions as categoryOptions,
  getSubcategoryOptionsForCategories,
  joinArtValues,
  splitArtValues,
} from '@/lib/artistCategories';
import { API_BASE_URL as API } from '@/lib/apiBase';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const readApiMessage = (err, fallback) => {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
};

const emptyProfile = {
  name: '',
  email: '',
  password: '',
  date_of_birth: '',
  signup_intent: 'artist',
  mobile: '',
  category: '',
  category_other: '',
  subcategory: '',
  subcategory_other: '',
  city: '',
  instagram: '',
  spotify: '',
  youtube: '',
  smart_fit: '',
  bio: '',
  image_url: '',
};

const PasswordToggleButton = ({ visible, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
    aria-label={visible ? 'Hide password' : 'Show password'}
    title={visible ? 'Hide password' : 'Show password'}
  >
    {visible ? (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 3l18 18" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M10.58 10.58A2 2 0 0012 14a2 2 0 001.42-.58" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9.88 5.09A10.94 10.94 0 0112 4.91c5.05 0 8.27 4.14 9 5.09a13.16 13.16 0 01-3.06 3.37M6.1 6.1A13.23 13.23 0 003 12c.73.95 3.95 5.09 9 5.09a10.9 10.9 0 004.11-.79" />
      </svg>
    ) : (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M2.46 12C3.73 7.94 7.52 5 12 5c4.48 0 8.27 2.94 9.54 7-1.27 4.06-5.06 7-9.54 7-4.48 0-8.27-2.94-9.54-7z" />
        <circle cx="12" cy="12" r="3" strokeWidth="1.8" />
      </svg>
    )}
  </button>
);

const CaptchaBox = ({ onVerify }) => {
  const [containerId] = useState(() => `artist-turnstile-${Math.random().toString(36).slice(2)}`);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;

    let cancelled = false;
    let widgetId = null;

    const renderTurnstile = () => {
      const container = document.getElementById(containerId);
      if (cancelled || !window.turnstile || !container) return;
      widgetId = window.turnstile.render(`#${containerId}`, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token) => onVerify(token),
        'expired-callback': () => onVerify(''),
        'error-callback': () => onVerify(''),
      });
      setLoaded(true);
    };

    if (!document.querySelector('script[data-turnstile-script]')) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.turnstileScript = 'true';
      script.onload = renderTurnstile;
      document.body.appendChild(script);
    } else {
      renderTurnstile();
    }

    return () => {
      cancelled = true;
      onVerify('');
      if (window.turnstile && widgetId !== null) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [containerId, onVerify]);

  if (!TURNSTILE_SITE_KEY) {
    return <p className="text-xs text-red-600 mt-3">CAPTCHA is not configured yet.</p>;
  }

  return (
    <div className="mt-4 flex justify-center min-h-[70px]">
      <div id={containerId} />
      {!loaded && <span className="text-sm text-gray-500">Loading CAPTCHA...</span>}
    </div>
  );
};

const JoinNowModal = ({ isOpen, onClose, onSuccess, initialProof }) => {
  const { setArtist, refreshArtist } = useArtistAuth();
  const navigate = useNavigate();
  const [flow, setFlow] = useState('otp');
  const [otpStep, setOtpStep] = useState(initialProof ? 3 : 1);
  const [verifiedProof, setVerifiedProof] = useState(initialProof || null);
  const [mobile, setMobile] = useState(initialProof?.mobile || '');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [formData, setFormData] = useState(() => ({ ...emptyProfile, email: initialProof?.email || '', mobile: initialProof?.mobile || '' }));
  const [policyConsent, setPolicyConsent] = useState(emptyPolicyConsent);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const categories = Object.keys(categoryOptions).filter((category) => category !== 'Other');
  const selectedCategories = splitArtValues(formData.category);
  const selectedSubcategories = splitArtValues(formData.subcategory);
  const availableSubcategories = getSubcategoryOptionsForCategories(selectedCategories);

  useEffect(() => {
    if (isOpen) return;
    setFlow('otp');
    setOtpStep(1);
    setMobile('');
    setLoginEmail('');
    setLoginPassword('');
    setFormData(emptyProfile);
    setPolicyConsent(emptyPolicyConsent);
    setCaptchaToken('');
    setCaptchaKey((value) => value + 1);
    setError('');
    setStatusMessage('');
    setLoading(false);
  }, [isOpen]);

  const resetCaptcha = () => {
    setCaptchaToken('');
    setCaptchaKey((value) => value + 1);
  };

  const completeAuth = async (artistData, successPayload) => {
    try {
      await axios.post(`${API}/collaborator-auth/logout`, {}, { withCredentials: true });
    } catch (logoutError) {
      // Ignore stale collaborator session cleanup failures.
    }
    setArtist(artistData);
    const refreshedArtist = await refreshArtist();
    if (!refreshedArtist) {
      setArtist(artistData);
    }
    onClose();
    navigate('/my-account', { replace: true });
    if (onSuccess) onSuccess(successPayload || { artist: artistData });
  };

  const requireCaptcha = () => {
    if (!TURNSTILE_SITE_KEY) {
      setError('CAPTCHA is not configured yet.');
      return false;
    }
    if (!captchaToken) {
      setError('Please complete the CAPTCHA verification');
      return false;
    }
    return true;
  };

  const handleEmailLogin = async (event) => {
    event.preventDefault();
    if (!requireCaptcha()) return;
    setLoading(true);
    setError('');
    setStatusMessage('');

    try {
      const response = await axios.post(
        `${API}/artist-auth/email-login`,
        { email: loginEmail, password: loginPassword, turnstile_token: captchaToken },
        { withCredentials: true }
      );
      await completeAuth(response.data, { artist: response.data, message: 'Login successful' });
    } catch (err) {
      setError(readApiMessage(err, 'Unable to sign in.'));
      resetCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const buildRegistrationPayload = () => ({
    ...formData,
    mobile: formData.mobile.trim() || undefined,
    category: joinArtValues([...splitArtValues(formData.category), ...splitArtValues(formData.category_other)]),
    subcategory: joinArtValues([...splitArtValues(formData.subcategory), ...splitArtValues(formData.subcategory_other)]),
    privacy_policy_accepted: Boolean(policyConsent.privacy),
    terms_accepted: Boolean(policyConsent.terms),
    trust_safety_accepted: Boolean(policyConsent.trust),
    turnstile_token: captchaToken,
  });

  const hasRequiredPortfolioLink = () =>
    [formData.instagram, formData.spotify, formData.youtube].some((value) => String(value || '').trim());

  const handleOtpRegister = async (event) => {
    event.preventDefault();
    if (!hasAcceptedAllPolicies(policyConsent)) {
      setError('Please read and accept the Privacy Policy, Terms & Conditions, and Trust & Safety rules before signup.');
      return;
    }
    if (!formData.city.trim()) {
      setError('City is required.');
      return;
    }
    if (!hasRequiredPortfolioLink()) {
      setError('Please add at least one social or portfolio link for verification.');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const response = await axios.post(
        `${API}/artists/register`,
        { ...buildRegistrationPayload(), mobile, verified_mobile_token: verifiedProof?.verified_mobile_token, turnstile_token: undefined },
        { withCredentials: true }
      );
      await completeAuth(response.data.artist, response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setError('');

    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await axios.post(`${API}/uploads/profile-image`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setFormData((current) => ({ ...current, image_url: response.data.image_url }));
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => {
      return { ...current, [name]: value };
    });
  };

  const toggleCategory = (category) => {
    setFormData((current) => {
      const currentCategories = splitArtValues(current.category);
      const nextCategories = currentCategories.includes(category)
        ? currentCategories.filter((item) => item !== category)
        : [...currentCategories, category];
      const allowedSubcategories = new Set(getSubcategoryOptionsForCategories(nextCategories));
      const nextSubcategories = splitArtValues(current.subcategory).filter((item) => allowedSubcategories.has(item));
      return {
        ...current,
        category: joinArtValues(nextCategories),
        subcategory: joinArtValues(nextSubcategories),
      };
    });
  };

  const toggleSubcategory = (subcategory) => {
    setFormData((current) => {
      const currentSubcategories = splitArtValues(current.subcategory);
      const nextSubcategories = currentSubcategories.includes(subcategory)
        ? currentSubcategories.filter((item) => item !== subcategory)
        : [...currentSubcategories, subcategory];
      return { ...current, subcategory: joinArtValues(nextSubcategories) };
    });
  };

  const switchFlow = (nextFlow) => {
    setFlow(nextFlow);
    setError('');
    resetCaptcha();
  };

  const updatePolicyConsent = (key, checked) => {
    setPolicyConsent((current) => ({ ...current, [key]: checked }));
    setError('');
  };

  if (!isOpen) return null;

  const renderProfileForm = () => (
    <>
      <input name="name" value={formData.name} onChange={handleChange} required placeholder="Full name" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <input name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="Email address" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />

      <div>
        <label className="mb-1 block text-sm font-semibold text-gray-700">Date of birth *</label>
        <input name="date_of_birth" type="date" value={formData.date_of_birth} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
        <p className="mt-1 text-xs text-gray-500">Required for age safety. Under-18 learner consent is planned for a later Learn phase.</p>
      </div>
      <input name="mobile" value={mobile} readOnly placeholder="Mobile number" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
              <ArtistTaxonomyFields value={formData} onChange={setFormData} />
      <input name="city" value={formData.city} onChange={handleChange} required placeholder="City *" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <input name="instagram" value={formData.instagram} onChange={handleChange} placeholder="Instagram profile URL" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <input name="spotify" value={formData.spotify} onChange={handleChange} placeholder="Spotify artist/profile URL" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <input name="youtube" value={formData.youtube} onChange={handleChange} placeholder="YouTube profile or channel URL" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <input name="smart_fit" value={formData.smart_fit} onChange={handleChange} placeholder="Preferred work: weddings, workshops, school events, brand shows" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
      <p className="text-xs text-gray-500 -mt-2">
        Add at least one portfolio link such as Instagram, Spotify, or YouTube so admin can verify your work.
        <span className="block mt-1">कम से कम एक portfolio link जोड़ें। Admin review के बाद link profile पर दिखेगा।</span>
      </p>
      <div>
        <input name="image_url" value={formData.image_url} onChange={handleChange} placeholder="Profile image URL" className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-2" />
        <input type="file" accept="image/*" onChange={handleImageUpload} className="w-full text-sm" />
        {uploadingImage && <p className="text-sm text-[#FF6F00] mt-1">Uploading image...</p>}
      </div>
      <textarea name="bio" value={formData.bio} onChange={handleChange} required rows="4" placeholder="Bio" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
    </>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 px-3 py-4 sm:p-6 overflow-y-auto" data-testid="join-now-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100vh-3rem)] overflow-y-auto my-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center gap-4 mb-5">
            <h2 className="text-2xl font-bold gradient-text">Artist Sign In / Join</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700" aria-label="Close">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="mb-4 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-gray-700">
            Existing accounts: sign in with your registered email. Add and verify your mobile number from your account after signing in.
          </div>
          <div className="mb-4">
            <LaunchTrustSignals compact />
          </div>

          <div className="mb-4 rounded-2xl sm:rounded-full bg-gray-100 p-1">
            <button type="button" onClick={() => switchFlow('otp')} className="btn-primary w-full mb-2">Email / WhatsApp code</button>
            <button type="button" onClick={() => switchFlow('email')} className="w-full rounded-2xl sm:rounded-full bg-white px-3 sm:px-4 py-2 text-xs sm:text-sm font-semibold text-[#FF6F00] shadow">Email + Password</button>
          </div>

          {error && <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}
          {statusMessage && <div className="bg-green-100 border border-green-300 text-green-800 px-4 py-3 rounded mb-4">{statusMessage}</div>}

          {flow === 'email' && (
            <form onSubmit={handleEmailLogin} className="space-y-4">
                  <input type="email" value={loginEmail} onChange={(event) => setLoginEmail(event.target.value)} required placeholder="Email address" className="w-full px-4 py-3 border border-gray-300 rounded-lg" />
                  <div className="relative">
                    <input type={showLoginPassword ? 'text' : 'password'} value={loginPassword} onChange={(event) => setLoginPassword(event.target.value)} required placeholder="Password" className="w-full px-4 py-3 pr-20 border border-gray-300 rounded-lg" />
                    <PasswordToggleButton visible={showLoginPassword} onClick={() => setShowLoginPassword((prev) => !prev)} />
                  </div>
                  <div className="flex justify-end">
                    <Link
                      to={`/forgot-password?account=artist&email=${encodeURIComponent(loginEmail.trim())}`}
                      onClick={onClose}
                      className="text-sm font-semibold text-[#FF6F00] hover:text-[#E66300]"
                    >
                      Forgot Password?
                    </Link>
                  </div>
                  <CaptchaBox key={`artist-login-${captchaKey}`} onVerify={setCaptchaToken} />
                  <button type="submit" disabled={loading || !TURNSTILE_SITE_KEY || !captchaToken} className="btn-primary w-full">{loading ? 'Signing in...' : 'Sign In'}</button>
                </form>
          )}

          {flow === 'otp' && otpStep === 1 && <OTPEntry onProof={(proof, selectedRole) => {
            if (selectedRole !== 'artist') { onClose(); navigate('/sign-in', { state: { proof, selectedRole } }); return; }
            setVerifiedProof(proof); setMobile(proof.mobile);
            setFormData(current => ({ ...current, mobile: proof.mobile, email: proof.email || current.email }));
            setOtpStep(3);
          }} />}

          {flow === 'otp' && otpStep === 3 && (
            <form onSubmit={handleOtpRegister} className="space-y-4">
              <p className="rounded-xl bg-gray-50 px-4 py-3 text-sm text-gray-600">
                Complete your artist profile for admin review. Add city, bio, image, and at least one portfolio link.
                <span className="block mt-2 text-xs leading-5">Admin review के लिए profile पूरी करें। शहर, bio, photo और एक portfolio link जरूर जोड़ें।</span>
              </p>
              {renderProfileForm()}
              <PolicyConsentChecklist value={policyConsent} onChange={updatePolicyConsent} />
              <button type="submit" disabled={loading || !hasAcceptedAllPolicies(policyConsent)} className="btn-primary w-full">{loading ? 'Saving...' : 'Complete Registration'}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default JoinNowModal;
